import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH17_QUESTIONS } from "../data/neetPhysicsCh17Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 17: Electrostatic Potential and Capacitance
// (NCERT Class 12 Physics). Content is drafted from the NCERT syllabus topic
// list; each concept has an explanation plus a worked example. NOT YET
// human-reviewed by a subject-matter expert — treat as a first draft and
// verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Electric Potential", "Potential Energy & Equipotential Surfaces", "Conductors & Electrostatics", "Capacitance", "Capacitor Combinations & Energy"];

const SECTION_COLORS = {
  "Electric Potential":                            "#2563eb",
  "Potential Energy & Equipotential Surfaces":      "#7c3aed",
  "Conductors & Electrostatics":                    "#dc2626",
  "Capacitance":                                    "#16a34a",
  "Capacitor Combinations & Energy":                "#d97706",
};

const CONCEPTS = [
  // ── Electric Potential ──────────────────────────────────────────────────────
  { section: "Electric Potential", title: "Electric potential - definition (V = W/q)",
    explanation: "Electric potential at a point is the work done in bringing a unit positive test charge from infinity to that point, moving it quasi-statically (without acceleration) so no energy goes into kinetic energy. V = W/q0, measured in volts (1 V = 1 J/C). Potential is a SCALAR, and is taken to be zero at infinity by convention.",
    example: `V = W / q0     (work done per unit charge, brought from infinity)

A charge q = 4 x 10^-9 C is brought from infinity to point P;
work done against the field = 8 x 10^-5 J.
V = W / q = (8 x 10^-5) / (4 x 10^-9) = 2 x 10^4 V` },
  { section: "Electric Potential", title: "Potential due to a point charge",
    explanation: "The electric potential at a distance r from a point charge Q (in vacuum) is V = kQ/r, where k = 9 x 10^9 N.m2/C2. V is positive if Q is positive and negative if Q is negative. Unlike the field (which falls off as 1/r2), potential falls off as 1/r.",
    example: `V = k Q / r        (k = 9 x 10^9 N.m^2/C^2)

Q = 2 x 10^-7 C, r = 0.1 m
V = (9 x 10^9)(2 x 10^-7) / 0.1
V = 1800 / 0.1 = 1.8 x 10^4 V` },
  { section: "Electric Potential", title: "Potential due to an electric dipole",
    explanation: "For a short dipole of moment p = q(2a): on the AXIAL line (r >> a), V = kp/r2; on the EQUATORIAL line, V = 0 exactly (equidistant from +q and -q, so the two potentials cancel); at a general point making angle theta with the dipole axis (r >> a), V = k p cos(theta) / r2. Note dipole potential falls off as 1/r2, faster than a single charge's 1/r.",
    example: `Axial (r>>a):      V = k p / r^2
Equatorial:         V = 0                (always, exactly)
General point:      V = k p cos(theta) / r^2

p = 4 x 10^-9 C.m, r = 0.1 m, axial point:
V = (9x10^9)(4x10^-9) / (0.1)^2 = 36 / 0.01 = 3.6 x 10^3 V` },
  { section: "Electric Potential", title: "Potential due to a system of point charges (superposition)",
    explanation: "Since potential is a scalar, the net potential at a point due to several point charges is the ALGEBRAIC (signed) sum of the potentials due to each charge individually -- no angles or vector components are needed, unlike for the electric field.",
    example: `V(net) = k [ q1/r1 + q2/r2 + q3/r3 + ... ]

P is 0.2 m from +4uC and 0.1 m from -2uC:
V = (9x10^9)[ (4x10^-6/0.2) + (-2x10^-6/0.1) ]
V = (9x10^9)[ 2x10^-5 - 2x10^-5 ] = 0 V` },
  { section: "Electric Potential", title: "Potential difference and work done (W = qΔV)",
    explanation: "The work done in moving a charge q from point A to point B equals q times the potential difference: W(A to B, by the field) = q(V_A - V_B). If a positive charge moves from high potential to low potential, the field does POSITIVE work on it; moving it the other way requires external work.",
    example: `W = q (V_A - V_B)

q = 5 x 10^-6 C moved from A (V_A = 120 V) to B (V_B = 50 V):
W = (5 x 10^-6)(120 - 50) = (5 x 10^-6)(70) = 3.5 x 10^-4 J` },

  // ── Potential Energy & Equipotential Surfaces ───────────────────────────────
  { section: "Potential Energy & Equipotential Surfaces", title: "Relation between electric field and potential (E = -dV/dr)",
    explanation: "The electric field is the negative gradient of potential: E = -dV/dr. The field points in the direction in which potential DECREASES most steeply, and its magnitude equals the rate of that decrease. In a uniform field, |E| = |ΔV| / Δr along the field direction.",
    example: `E = - dV / dr

Potential falls from 100 V to 40 V over 3 cm along the field:
|E| = |100 - 40| / 0.03 = 60 / 0.03 = 2000 V/m = 2 x 10^3 V/m` },
  { section: "Potential Energy & Equipotential Surfaces", title: "Equipotential surfaces - definition and properties",
    explanation: "An equipotential surface is a surface on which potential has the same value everywhere. Properties: (1) the electric field is always PERPENDICULAR to it; (2) no work is done moving a charge along it; (3) two equipotential surfaces can NEVER intersect (a point can't have two potentials at once); (4) for an isolated point charge they are concentric spheres; (5) surfaces spaced closer together indicate a STRONGER field.",
    example: `Point charge -> equipotential surfaces are concentric spheres
               V = kQ/r is constant on each sphere of radius r

Closer-spaced equipotential surfaces  <->  stronger local E
   (since E = -dV/dr, smaller dr for the same dV means larger E)` },
  { section: "Potential Energy & Equipotential Surfaces", title: "Potential energy of a system of two point charges",
    explanation: "The electrostatic potential energy of a pair of point charges q1, q2 separated by r is U = k q1 q2 / r, taking U = 0 at infinite separation. U is negative for unlike (attracting) charges and positive for like (repelling) charges.",
    example: `U = k q1 q2 / r

q1 = +3 x 10^-6 C, q2 = -2 x 10^-6 C, r = 0.3 m
U = (9x10^9)(3x10^-6)(-2x10^-6) / 0.3
U = (-5.4 x 10^-2) / 0.3 = -0.18 J   (negative: unlike charges)` },
  { section: "Potential Energy & Equipotential Surfaces", title: "Potential energy of a system of three point charges",
    explanation: "For three point charges, total potential energy is the sum of the pairwise potential energies: U = k(q1q2/r12 + q2q3/r23 + q1q3/r13). This generalizes to any number of charges by summing over every distinct pair.",
    example: `U = k ( q1q2/r12 + q2q3/r23 + q1q3/r13 )

Equilateral triangle, side a = 0.1 m, each charge +1 x 10^-6 C:
All pairwise distances = a, so U = 3 k q^2 / a
U = 3 (9x10^9)(1x10^-6)^2 / 0.1 = 3(9x10^9)(1x10^-12)/0.1
U = 3 x 9 x 10^-3 / 0.1 = 0.27 J` },
  { section: "Potential Energy & Equipotential Surfaces", title: "Potential energy of a dipole in an external field",
    explanation: "A dipole of moment p in a uniform external field E, at angle theta to the field, has potential energy U = -pE cos(theta) (minimum, most stable, at theta = 0; maximum, least stable, at theta = 180 deg). Work done by an external agent rotating the dipole from theta1 to theta2 is W = pE(cos theta1 - cos theta2).",
    example: `U(theta) = - p E cos(theta)

p = 2 x 10^-8 C.m, E = 5 x 10^4 N/C, theta = 60 deg
U = -(2x10^-8)(5x10^4)(cos 60) = -(1x10^-3)(0.5) = -5 x 10^-4 J` },

  // ── Conductors & Electrostatics ─────────────────────────────────────────────
  { section: "Conductors & Electrostatics", title: "Electric field inside a conductor is zero",
    explanation: "In electrostatic equilibrium, free charges in a conductor redistribute themselves on its surface until the net electric field EVERYWHERE inside the conductor is zero. If it weren't zero, the free charges would keep experiencing a force and keep moving -- contradicting the assumption of equilibrium. This holds even if an external field is applied, or the conductor carries its own net charge.",
    example: `Conductor in equilibrium:  E(inside) = 0   (always)

Placed in external field E0:
Induced surface charges create an internal field that
exactly cancels E0, so net field inside remains zero.` },
  { section: "Conductors & Electrostatics", title: "Conductor surface is equipotential; field just outside is perpendicular",
    explanation: "Just outside a conductor's surface, the electric field must be PERPENDICULAR to the surface -- a tangential component would push surface charges along the surface, violating equilibrium. Since E = 0 throughout the interior, no work is needed to move charge between any two points inside (or on) the conductor, so the entire conductor -- surface and interior alike -- is at one single potential.",
    example: `Just outside surface:  E perpendicular to surface (always)
Inside + on surface:    V = constant (equipotential volume)

(A tangential E-component would mean unbalanced surface
force -> charges would still be moving -> not equilibrium)` },
  { section: "Conductors & Electrostatics", title: "Charge distribution on a conductor surface",
    explanation: "For an isolated charged conductor, all excess charge resides on the OUTER surface (none in the interior, since E = 0 there). The surface charge density is not uniform for an irregularly shaped conductor -- it is GREATEST where the surface curvature is greatest (sharp points and edges), which is why the field is strongest near such points.",
    example: `Sharper curvature (smaller radius)  ->  higher surface charge
                                          density (sigma)
                                     ->  stronger local E just outside

Basis of the lightning conductor: charge (and field) concentrates
at the sharp tip, encouraging controlled discharge to the air.` },
  { section: "Conductors & Electrostatics", title: "Electrostatic shielding",
    explanation: "A hollow conductor shields the region enclosed by its cavity from any external electric field, regardless of the shape of the conductor or the cavity, and regardless of charges placed outside. Induced charges on the outer surface adjust so the field inside the cavity stays zero (or unaffected by the external field). This is the working principle of a Faraday cage.",
    example: `Hollow conductor + external field E0 applied outside:
   Field inside the cavity = 0   (unaffected by E0)

Applications: cars/aircraft in lightning, coaxial cables,
sensitive electronic equipment enclosures` },

  // ── Capacitance ──────────────────────────────────────────────────────────────
  { section: "Capacitance", title: "Capacitance - definition (C = Q/V)",
    explanation: "Capacitance is the ratio of the charge Q stored to the potential difference V produced: C = Q/V. Its SI unit is the farad (F); since 1 F is very large for practical devices, microfarad (uF, 10^-6 F) and picofarad (pF, 10^-12 F) are common. Capacitance depends only on the geometry (and any dielectric) of the capacitor, not on Q or V individually.",
    example: `C = Q / V     (SI unit: farad, F = C/V)

Q = 6 x 10^-4 C stored at V = 300 V
C = (6 x 10^-4) / 300 = 2 x 10^-6 F = 2 uF` },
  { section: "Capacitance", title: "Parallel plate capacitor (C = ε₀A/d)",
    explanation: "For two parallel plates each of area A separated by distance d, with vacuum (or air) between them, C = ε0 A / d, where ε0 = 8.85 x 10^-12 F/m. Capacitance INCREASES with larger plate area and DECREASES with larger separation.",
    example: `C = eps0 . A / d      (eps0 = 8.85 x 10^-12 F/m)

A = 0.02 m^2, d = 1 x 10^-3 m
C = (8.85x10^-12)(0.02) / (1x10^-3)
C = 1.77 x 10^-10 F = 177 pF` },
  { section: "Capacitance", title: "Effect of a dielectric on capacitance",
    explanation: "Inserting a dielectric of dielectric constant K, completely filling the gap between the plates, INCREASES the capacitance to C' = K C0 (where C0 is the capacitance with vacuum/air). For a parallel plate capacitor this becomes C' = K eps0 A / d. K is always > 1 for a real dielectric (K = 1 for vacuum).",
    example: `C' = K . C0     (dielectric fills the gap completely)

C0 = 2 uF (air), dielectric with K = 5 inserted:
C' = 5 x 2 uF = 10 uF` },
  { section: "Capacitance", title: "Dielectric polarization and the reduction of internal field",
    explanation: "A dielectric has no free charges to conduct, but an external field slightly displaces (or aligns) the bound charges within each molecule -- polarization -- producing induced surface charges on the dielectric. Their field opposes the external field, so the NET field inside the dielectric is reduced to E = E0 / K, where K is the dielectric constant.",
    example: `E(inside dielectric) = E0 / K

E0 = 3 x 10^5 V/m, K = 3
E(inside) = (3 x 10^5) / 3 = 1 x 10^5 V/m` },

  // ── Capacitor Combinations & Energy ─────────────────────────────────────────
  { section: "Capacitor Combinations & Energy", title: "Capacitors in series",
    explanation: "In series, each capacitor carries the SAME charge Q, while the total voltage divides among them. The reciprocal of the equivalent capacitance is the sum of reciprocals: 1/C_eq = 1/C1 + 1/C2 + ... -- so C_eq is always SMALLER than the smallest individual capacitance.",
    example: `1/C_eq = 1/C1 + 1/C2 + ...

C1 = 2 uF, C2 = 3 uF (series)
1/C_eq = 1/2 + 1/3 = 3/6 + 2/6 = 5/6
C_eq = 6/5 = 1.2 uF` },
  { section: "Capacitor Combinations & Energy", title: "Capacitors in parallel",
    explanation: "In parallel, each capacitor has the SAME potential difference V across it, while the total charge divides among them according to Q = CV for each. The equivalent capacitance is simply the sum: C_eq = C1 + C2 + ... -- always LARGER than the largest individual capacitance.",
    example: `C_eq = C1 + C2 + ...

C1 = 2 uF, C2 = 3 uF (parallel)
C_eq = 2 + 3 = 5 uF` },
  { section: "Capacitor Combinations & Energy", title: "Energy stored in a capacitor",
    explanation: "Charging a capacitor requires work done against the growing potential difference; the energy stored is U = (1/2)CV^2 = (1/2)QV = Q^2/(2C) -- these three forms are all equivalent (via Q = CV). Since U depends on Q^2 (or V^2), doubling the charge (at constant C) quadruples the stored energy.",
    example: `U = (1/2) C V^2 = (1/2) Q V = Q^2 / (2C)

C = 4 x 10^-6 F, V = 100 V
U = (1/2)(4x10^-6)(100)^2 = (1/2)(4x10^-6)(10^4)
U = (1/2)(4x10^-2) = 2 x 10^-2 J = 20 mJ` },
  { section: "Capacitor Combinations & Energy", title: "Energy density in the electric field",
    explanation: "The energy stored in a capacitor can be thought of as stored in the ELECTRIC FIELD itself, spread through the volume between the plates. The energy density (energy per unit volume) is u = (1/2) eps0 E^2 in vacuum -- a result that holds generally for any electrostatic field, not just inside a capacitor.",
    example: `u = (1/2) eps0 E^2     (eps0 = 8.85 x 10^-12 F/m)

E = 1 x 10^5 V/m
u = (1/2)(8.85x10^-12)(1x10^5)^2
u = (1/2)(8.85x10^-12)(1x10^10) = (1/2)(8.85x10^-2)
u = 4.425 x 10^-2 J/m^3  (approx 4.4 x 10^-2 J/m^3)` },
  { section: "Capacitor Combinations & Energy", title: "Van de Graaff generator",
    explanation: "The Van de Graaff generator builds up very high potentials (several million volts) using electrostatic shielding: charge sprayed onto a moving insulated belt is carried up and transferred to a large hollow conducting sphere, where -- since charge on an isolated conductor resides only on the outer surface -- it keeps flowing onto the sphere regardless of how much charge (and potential) the sphere already holds. The maximum potential is ultimately limited by the dielectric breakdown of the surrounding air.",
    example: `V(max) = E(breakdown) x R      (V = E.R at the sphere's surface)

Air breaks down at about E ~ 3 x 10^6 V/m.
For a sphere of radius R = 1 m:
V(max) ~ (3 x 10^6)(1) = 3 x 10^6 V  (about 3 million volts)` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh17Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh17Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh17Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH17_QUESTIONS : NEET_PHYSICS_CH17_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 17</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Electrostatic Potential and Capacitance</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12, Ch. 2 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH17_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH17_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
